import { Link } from "react-router-dom";
import { ArrowUpRight, Hammer, ShieldCheck, Sparkles } from "lucide-react";
import { TESTIMONIALS } from "@/lib/services";
import Reveal from "@/components/Reveal";
import Seo, { LOCAL_BUSINESS_LD, breadcrumbLd } from "@/components/Seo";

const VALUES = [
  {
    icon: Hammer,
    title: "Craftsmanship",
    text: "Every joint, every finish, every fixing — done by hand, done properly. No shortcuts, ever.",
  },
  {
    icon: ShieldCheck,
    title: "Reliability",
    text: "Turn up when we say, tidy up every night, and finish on the day we quoted. Trades you can trust.",
  },
  {
    icon: Sparkles,
    title: "Bespoke",
    text: "No two homes are the same. Every project is designed and built around how you actually live.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 md:pt-40">
      <Seo
        title="About Woodland Joinery — Sheffield Joiners & Builders You Can Trust"
        description="Woodland Joinery & Building are Sheffield-based joiners and builders led by Sean and Rocky, delivering bespoke joinery and full building projects across South Yorkshire and Derbyshire."
        path="/about"
        jsonLd={[
          LOCAL_BUSINESS_LD,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-end">
          <Reveal className="md:col-span-7">
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#4A5550]">
              About Us
            </p>
            <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-[#1F3B2D] leading-[0.95]">
              A joiner, a builder, <em className="text-[#C8A24B] not-italic font-light">and a shared passion for timber.</em>
            </h1>
          </Reveal>
          <Reveal delay={160} className="md:col-span-5">
            <p className="text-lg leading-relaxed text-[#4A5550]">
              Woodland Joinery &amp; Building was founded on a simple idea:
              treat every job like it’s your own home. Sean brings decades of
              fine joinery experience; Rocky leads the building side. Together,
              we handle the entire journey — design, timber selection, build and
              finish — under one roof.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Image + story */}
      <section className="border-t border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <Reveal className="md:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1497219055242-93359eeed651?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxiZXNwb2tlJTIwY2FycGVudHJ5JTIwd29vZHdvcmtpbmclMjBjcmFmdHNtYW5zaGlwfGVufDB8fHx8MTc4MzAyNjA4OXww&ixlib=rb-4.1.0&q=85"
                alt="Bespoke joinery chisel work"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={160} className="md:col-span-6">
            <span className="gold-rule" />
            <h2 className="mt-6 font-display text-4xl md:text-5xl text-[#1F3B2D]">
              Rooted in Sheffield.
            </h2>
            <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-[#4A5550]">
              <p>
                We’re based in Sheffield and take on projects across South
                Yorkshire and Derbyshire — from a set of alcove shelves in Nether
                Edge to a full rear extension in Dronfield.
              </p>
              <p>
                Our workshop lets us design, build and spray-finish bespoke
                joinery in-house. On site, we run our own building team — so
                whether it’s foundations, brickwork, roofing or the final coat
                of paint, you deal with one crew you can trust.
              </p>
              <p>
                It’s a small, close-knit team by design. It means we can be
                choosy about the jobs we take on, and unfailingly obsessive about
                the ones we deliver.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#1F3B2D] text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="gold-rule" />
            <p className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#C8A24B]">
              What we stand for
            </p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl">
              Three values. Every project.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-8 md:gap-10">
            {VALUES.map((v, i) => (
              <Reveal
                key={v.title}
                delay={i * 140}
                className="border border-white/10 p-8 md:p-10 hover:border-[#C8A24B] transition"
              >
                <div className="h-12 w-12 border border-[#C8A24B] flex items-center justify-center text-[#C8A24B]">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl">
                  {v.title}
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials preview */}
      <section className="bg-[#FDFBF7] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-4">
            <span className="gold-rule" />
            <h2 className="mt-6 font-display text-4xl md:text-5xl text-[#1F3B2D] leading-tight">
              Trusted by homes across South Yorkshire.
            </h2>
          </Reveal>
          <div className="md:col-span-8 space-y-10">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 100} className="border-t border-[#E5E0D8] pt-8">
                <p className="font-display text-xl md:text-2xl text-[#1F3B2D] leading-relaxed">
                  “{t.quote}”
                </p>
                <div className="mt-4 text-sm text-[#4A5550]">
                  <span className="text-[#C8A24B] tracking-widest uppercase text-[11px]">
                    {t.author}
                  </span>
                  <span className="mx-3 opacity-40">·</span>
                  {t.location}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 border-t border-[#E5E0D8] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl text-[#1F3B2D]">
              Let’s talk about your project.
            </h2>
            <Link to="/contact" data-testid="about-cta-quote" className="btn-primary mt-8">
              Request a Free Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
